import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {forkJoin, Observable, of, ReplaySubject} from "rxjs";

import {PartnerFacility} from "../../../shared/interface/partner-facility";
import {
  CityDobryAgent,
  FacilityDobryAgent,
  GooglePlaceDobryAgent, ImageResponsive
} from "../../../shared/interface/facility-dobry-agent";
import {Ofwca} from "../../../shared/interface/ofwca";
import {UserApplicationPermission, UserPermissions} from "../../../shared/interface/user-permissions";
import {UserService} from "../../../shared/services/user.service";
import {AuthService} from "../../../shared/services/auth.service";
import {catchError, first, map, shareReplay, switchMap, take, takeUntil} from "rxjs/operators";
import {OfwcaWithUser360Profiles, User} from "../../../shared/interface/user";
import {Router} from "@angular/router";
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {MatTableDataSource} from "@angular/material/table";
import {FormHelperService} from "../../../shared/services/form-helper.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {FacilityDobryAgentService} from "../../../shared/services/facility-dobry-agent.service";
import {AngularEditorConfig} from "@kolkov/angular-editor";
import {Partner} from "../../../shared/interface/partner";
import {Dictionary} from "../../../shared/interface/dictionary";
import {PARTNER_TAGS_GOOD_AGENT} from "../../../shared/services/dict.service";
import {ImageDragAndDropItem} from "../../../shared/components/image-drag-and-drop/image-drag-and-drop.component";
import {environment} from "../../../../environments/environment";

@Component({
  selector: 'app-facility-dobry-agent-add',
  templateUrl: './facility-dobry-agent-add.component.html',
  styleUrls: ['./facility-dobry-agent-add.component.scss']
})
export class FacilityDobryAgentAddComponent implements OnInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() facility: Observable<PartnerFacility>;
  @Input() facilityDobryAgent: Observable<FacilityDobryAgent>;
  @Input() ofwcas: Observable<Ofwca[]>;

  @Output() closeModification = new EventEmitter<any>();

  partnerFacility?: PartnerFacility = null;
  facilityDobryAgentObject?: FacilityDobryAgent = null;

  userPermissions: ReplaySubject<UserPermissions>;
  userApplicationPermission: ReplaySubject<UserApplicationPermission>;
  processingRequest = false;
  uploadingAgentImage: Record<string, boolean> = {};
  facilityDobryAgentForm: FormGroup;
  dobryAgentBaseUrl = environment.dobryAgentUrl;

  ofwcasDataSource: MatTableDataSource<OfwcaWithUser360Profiles> = new MatTableDataSource<OfwcaWithUser360Profiles>([]);
  displayedColumns: string[] = [
    'fullName',
    'status',
    'phone',
    'email',
    'portalDa',
    'blog',
    'edit',
    'avatar',
  ];

  private readonly user360ProfileCache = new Map<string, Observable<User | null>>();

  readonly openingHoursDays = [
    {key: 'poniedzialek', label: 'Poniedziałek'},
    {key: 'wtorek', label: 'Wtorek'},
    {key: 'sroda', label: 'Środa'},
    {key: 'czwartek', label: 'Czwartek'},
    {key: 'piatek', label: 'Piątek'},
    {key: 'sobota', label: 'Sobota'},
    {key: 'niedziela', label: 'Niedziela'},
  ];

  dayOffDateControl = new FormControl(null);
  dayOffDateFromControl = new FormControl(null);
  dayOffDateToControl = new FormControl(null);

  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '220px',
    minHeight: '160px',
    maxHeight: '400px',
    width: '100%',
    minWidth: '0',
    translate: 'yes',
    enableToolbar: true,
    showToolbar: true,
    placeholder: 'Wpisz opis placówki...',
    defaultParagraphSeparator: 'p',
    defaultFontName: 'Arial',
    sanitize: false,
    toolbarHiddenButtons: [
      [
        'strikeThrough',
        'subscript',
        'superscript',
        'justifyRight',
        'justifyFull',
        'indent',
        'outdent',
        'insertUnorderedList',
        'insertOrderedList',
        'heading',
        'fontName',
        'fontSize',
        'textColor',
        'backgroundColor',
        'customClasses',
        'link',
        'unlink',
        'insertImage',
        'insertVideo',
      ],
      [
        // 'toggleEditorMode'
      ]
    ]
  };

  constructor(
    private router: Router,
    private facilityDobryAgentService: FacilityDobryAgentService,
    private auth: AuthService,
    private userService: UserService,
    private fb: FormBuilder,
    private formHelper: FormHelperService,
    private snackBar: MatSnackBar,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.userApplicationPermission = auth.getUserApplicationPermission();

    this.facilityDobryAgentForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      type: [''],
      serviceType: [''],
      phoneNumber: [''],
      email: ['', Validators.email],
      status: [''],

      active: [false],

      address: this.fb.group({
        street: this.fb.control('', [Validators.required]),
        houseNumber: this.fb.control('', [Validators.required]),
        apartmentNumber: this.fb.control(''),
        uuid: this.fb.control('', [Validators.required]),
        city: this.fb.control('', [Validators.required]),
        zipCode: this.fb.control('', [Validators.required,
          Validators.minLength(6),
          Validators.pattern('^[0-9]{2,5}(:|\\-)?[0-9]{3,4}')]),
      }),

      openHours: this.fb.group({
        poniedzialek: this.generateOpeningHoursDayGroup(),
        wtorek: this.generateOpeningHoursDayGroup(),
        sroda: this.generateOpeningHoursDayGroup(),
        czwartek: this.generateOpeningHoursDayGroup(),
        piatek: this.generateOpeningHoursDayGroup(),
        sobota: this.generateOpeningHoursDayGroup(),
        niedziela: this.generateOpeningHoursDayGroup(),
      }),
      daysOffFromWork: this.fb.array([]),
      googlePlaceId: [''],
      googleBusinessUrl: [''],

      seoSlug: [''],
      seoTitle: [''],
      seoDescription: [''],
      seoKeywords: [''],

      facebook: [''],
      website: [''],

      affiliateCode: [''],

      parking: [false],
      accessibilityFeatures: [false],
      bestAgents: [false],

      subCategories: [[]],
      insuranceCompanies: [[]],
      paymentMethods: [[]],

      chronieInsuranceProducts: [[]],
      images: [[]],
      ofwcaPermissions: this.fb.array([]),
    });
  }

  private getUser360Profile(uuid: string): Observable<User | null> {
    if (!this.user360ProfileCache.has(uuid)) {
      this.user360ProfileCache.set(
        uuid,
        this.userService.get(uuid).pipe(
          catchError(() => of(null)),
          shareReplay(1)
        )
      );
    }

    return this.user360ProfileCache.get(uuid);
  }


  private generateOpeningHoursDayGroup(): FormGroup {
    return this.fb.group({
      openTime: [''],
      closeTime: [''],
      closed: [false],
    });
  }

  ngOnInit(): void {
    this.facilityDobryAgentService.getCityDobryAgentSubject()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((items) => {
      });
    this.facility
      .pipe(takeUntil(this.destroyed$))
      .subscribe((facility) => {
        this.partnerFacility = facility;
      });

    this.ofwcas
      .pipe(
        switchMap((ofwcas: Ofwca[]) => {
          const requests = (ofwcas ?? []).map((ofwca: Ofwca) => {
            const users360 = ofwca.user360 ?? [];

            if (!users360.length) {
              return of({
                ...ofwca,
                user360Profiles: [],
              } as OfwcaWithUser360Profiles);
            }

            const userRequests = users360
              .filter((user) => !!user.uuid)
              .map((user) => this.getUser360Profile(user.uuid));

            if (!userRequests.length) {
              return of({
                ...ofwca,
                user360Profiles: [],
              } as OfwcaWithUser360Profiles);
            }

            return forkJoin(userRequests).pipe(
              map((profiles) => ({
                ...ofwca,
                user360Profiles: profiles.filter((profile): profile is User => !!profile),
              } as OfwcaWithUser360Profiles))
            );
          });

          return requests.length ? forkJoin(requests) : of([]);
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe((ofwcas: OfwcaWithUser360Profiles[]) => {
        this.ofwcasDataSource.data = ofwcas;
        this.patchOfwcaPermissions(ofwcas);
      });

    this.facilityDobryAgent
      .pipe(takeUntil(this.destroyed$))
      .subscribe((facilityDobryAgent: FacilityDobryAgent) => {
        this.facilityDobryAgentObject = facilityDobryAgent;
        this.patchForm(facilityDobryAgent);
        this.patchOfwcaPermissions(this.ofwcasDataSource.data);
      });
  }

  private patchForm(facility: FacilityDobryAgent): void {
    if (!facility) {
      return;
    }
    this.facilityDobryAgentForm.patchValue({
      name: facility.name ?? '',
      description: facility.description ?? '',
      type: facility.type?.id ?? facility.type ?? '',
      serviceType: facility.serviceType?.id ?? facility.serviceType ?? '',
      phoneNumber: facility.phoneNumber ?? '',
      email: facility.email ?? '',
      status: facility.status?.id ?? facility.status ?? '',

      active: facility.active,

      googlePlaceId: facility.googlePlaceId ?? '',
      googleBusinessUrl: facility.googleBusinessUrl ?? '',

      seoSlug: facility.seoSlug ?? '',
      seoTitle: facility.seoTitle ?? '',
      seoDescription: facility.seoDescription ?? '',
      seoKeywords: facility.seoKeywords ?? '',

      facebook: facility.facebook ?? '',
      website: facility.website ?? '',

      affiliateCode: facility.affiliateCode ?? '',

      parking: !!facility.parking,
      accessibilityFeatures: !!facility.accessibilityFeatures,
      bestAgents: !!facility.bestAgents,

      subCategories: facility.subCategories?.map((item: any) => item.uuid ?? item.id) ?? [],
      insuranceCompanies: facility.insuranceCompanies?.map((item: any) => item.uuid ?? item.id) ?? [],
      paymentMethods: facility.paymentMethods?.map((item: any) => item.uuid ?? item.id) ?? [],
      images: this.mapFacilityImages(facility),
      chronieInsuranceProducts: facility.chronieInsuranceProducts?.map((item: any) => item.uuid ?? item.id) ?? [],
    });


    if (facility.address) {
      this.facilityDobryAgentForm.get('address')?.patchValue({
        street: facility.address.street ?? '',
        houseNumber: facility.address.houseNumber ?? '',
        apartmentNumber: facility.address.apartmentNumber ?? '',

        // UUID adresu
        uuid: facility.address.uuid ?? '',

        // UUID miasta z DobryAgent
        city: '',

        zipCode: facility.address.zipCode ?? '',
      }, { emitEvent: false });
    }
    this.patchCityByName(facility.address.city);
    this.patchOpeningHours(facility.openHours);
    this.patchDaysOffFromWork(facility.daysOffFromWork);
  }

  private patchOpeningHours(openHours: any): void {
    if (!openHours) {
      return;
    }

    this.openingHoursDays.forEach((day) => {
      const item = openHours[day.key];

      const openTime = this.normalizeTime(item?.openTime);
      const closeTime = this.normalizeTime(item?.closeTime);

      const closed =
        item?.openTime?.toLowerCase?.() === 'nieczynne'
        || item?.closeTime?.toLowerCase?.() === 'nieczynne'
        || (!openTime && !closeTime);

      const group = this.getOpeningHoursDayGroup(day.key);

      group.patchValue({
        openTime: closed ? '' : openTime,
        closeTime: closed ? '' : closeTime,
        closed,
      }, {
        emitEvent: false,
      });

      if (closed) {
        group.get('openTime')?.disable({ emitEvent: false });
        group.get('closeTime')?.disable({ emitEvent: false });
      } else {
        group.get('openTime')?.enable({ emitEvent: false });
        group.get('closeTime')?.enable({ emitEvent: false });
      }
    });
  }

  private normalizeTime(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    if (value.toLowerCase() === 'nieczynne') {
      return '';
    }

    const parts = value.split(':');

    if (parts.length < 2) {
      return '';
    }

    const hours = parts[0].padStart(2, '0');
    const minutes = parts[1].padStart(2, '0');

    return `${hours}:${minutes}`;
  }

  private normalizeUrl(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return '';
    }

    if (/^https?:\/\//i.test(trimmedValue)) {
      return trimmedValue;
    }

    return `https://${trimmedValue}`;
  }

  getOpeningHoursDayGroup(dayKey: string): FormGroup {
    return this.facilityDobryAgentForm.get(['openHours', dayKey]) as FormGroup;
  }

  onClosedChange(dayKey: string): void {
    const group = this.getOpeningHoursDayGroup(dayKey);

    const openTimeControl = group.get('openTime');
    const closeTimeControl = group.get('closeTime');

    if (group.get('closed')?.value) {
      openTimeControl?.setValue('');
      closeTimeControl?.setValue('');

      openTimeControl?.disable({ emitEvent: false });
      closeTimeControl?.disable({ emitEvent: false });
    } else {
      openTimeControl?.enable({ emitEvent: false });
      closeTimeControl?.enable({ emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  cancel(): void {
    if (!this.partnerFacility) {
      this.router.navigate(['/placowki']);
    } else {
      this.closeModification.emit();
    }
  }

  private prepareFacilityPayload(rawValue: any, asf24AddressUuid: string | null): any {
    const address = rawValue.address ?? {};
    const agents = this.prepareAgentsPayload(rawValue.ofwcaPermissions ?? []);
    return {
      uuid: this.facilityDobryAgentObject?.uuid ?? null,

      // dodatkowe pole dla endpointu ASF24, jeżeli go używasz po swojej stronie
      asf24AddressUuid,

      name: rawValue.name,
      description: rawValue.description ?? '',

      // EntityType Dictionary
      type: rawValue.type,
      serviceType: rawValue.serviceType,

      email: rawValue.email ?? '',
      phoneNumber: rawValue.phoneNumber ?? '',

      // CreateFacilityForm oczekuje adresu na głównym poziomie
      street: address.street ?? '',
      buildingNumber: address.houseNumber ?? '',
      apartmentNumber: address.apartmentNumber ?? '',
      city: address.city, // UUID miasta DobryAgent
      zipCode: address.zipCode ?? '',

      // CreateFacilityForm nie ma status, tylko active
      active: rawValue.active,

      website: this.normalizeUrl(rawValue.website),
      facebook: this.normalizeUrl(rawValue.facebook),

      parking: !!rawValue.parking,
      accessibilityFeatures: !!rawValue.accessibilityFeatures,
      bestAgents: !!rawValue.bestAgents,

      openHours: this.prepareOpeningHoursPayload(rawValue.openHours),

      daysOffFromWork: (rawValue.daysOffFromWork ?? []).filter(Boolean).sort(),
      agents,
      coordinators: agents.map((agent: any) => agent.ofwca24Id),

      paymentMethods: rawValue.paymentMethods ?? [],
      subCategories: rawValue.subCategories ?? [],
      insuranceCompanies: rawValue.insuranceCompanies ?? [],

      images: rawValue.images ?? [],

      seoSlug: rawValue.seoSlug ?? '',
      seoTitle: rawValue.seoTitle ?? '',
      seoDescription: rawValue.seoDescription ?? '',
      seoKeywords: rawValue.seoKeywords ?? '',

      deleted: false,

      googlePlaceId: rawValue.googlePlaceId ?? '',
      affiliateCode: rawValue.affiliateCode ?? '',
    };
  }

  save(): void {
    if (this.facilityDobryAgentForm.invalid) {
      this.facilityDobryAgentForm.markAllAsTouched();

      this.snackBar.open('Uzupełnij wymagane pola formularza', '', {
        duration: 2000,
      });

      return;
    }

    this.processingRequest = true;

    const rawValue = this.facilityDobryAgentForm.getRawValue();

    const asf24AddressUuid = this.partnerFacility?.address?.uuid
      ?? this.facilityDobryAgentObject?.asf24AddressUuid
      ?? rawValue.address?.uuid
      ?? null;

    const isEdit = !!this.facilityDobryAgentObject?.uuid;

    const payload = this.prepareFacilityPayload(rawValue, asf24AddressUuid);


    this.facilityDobryAgentService.sendForm(payload, isEdit ? asf24AddressUuid : null)
      .pipe(
        first(),
        switchMap(() => {
          return this.updateDobryAgentUsersPermissions(rawValue.ofwcaPermissions ?? []);
        })
      )
      .subscribe({
        next: () => {
          this.processingRequest = false;

          this.snackBar.open(
            isEdit
              ? 'Pomyślnie zaktualizowano wizytówkę DA i uprawnienia doradców'
              : 'Pomyślnie utworzono wizytówkę DA i uprawnienia doradców',
            '',
            {
              duration: 2000,
            }
          );

          this.closeModification.emit();
        },
        error: () => {
          this.processingRequest = false;

          this.snackBar.open('Podczas zapisu wizytówki lub uprawnień wystąpił błąd', '', {
            duration: 2000,
          });
        },
      });
  }

  private prepareOpeningHoursPayload(openHours: any): any {
    const payload = {};

    this.openingHoursDays.forEach((day) => {
      const item = openHours[day.key];

      payload[day.key] = {
        openTime: item.closed ? 'nieczynne' : item.openTime,
        closeTime: item.closed ? 'nieczynne' : item.closeTime,
      };
    });

    return payload;
  }

  onCitySelected(city: CityDobryAgent | null): void {
    const addressGroup = this.facilityDobryAgentForm.get('address') as FormGroup;

    if (!city) {
      addressGroup.patchValue({
        district: '',
        commune: '',
        state: '',
      }, { emitEvent: false });

      return;
    }

    addressGroup.patchValue({
      district: city.district,
      commune: city.community,
      state: city.voivodeship,
    }, { emitEvent: false });
  }

  private patchCityByName(cityName?: string | null): void {
    if (!cityName) {
      return;
    }

    this.facilityDobryAgentService.searchCityDobryAgent(cityName, 20, 1)
      .pipe(take(1))
      .subscribe((response) => {
        const cities = response.items ?? [];

        const city = cities.find((item) => {
          return item.name?.toLowerCase() === cityName.toLowerCase();
        }) ?? cities[0] ?? null;

        if (!city) {
          return;
        }

        this.facilityDobryAgentForm.get('address.city')?.setValue(city.uuid, {
          emitEvent: false,
        });

        this.onCitySelected(city);
      });
  }
  onGooglePlaceSelected(place: GooglePlaceDobryAgent | null): void {
    this.facilityDobryAgentForm.patchValue({
      googleBusinessUrl: place
        ? this.buildGoogleBusinessUrl(place.place_id)
        : '',
    }, { emitEvent: false });
  }

  private buildGoogleBusinessUrl(placeId: string): string {
    return `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(placeId)}`;
  }

  getOfwcaPermissionControl(index: number, controlName: string): FormControl {
    return this.getOfwcaPermissionGroup(index).get(controlName) as FormControl;
  }
  get ofwcaPermissionsArray(): FormArray {
    return this.facilityDobryAgentForm.get('ofwcaPermissions') as FormArray;
  }

  getOfwcaPermissionGroup(index: number): FormGroup {
    return this.ofwcaPermissionsArray.at(index) as FormGroup;
  }

  private buildOfwcaPermissionGroup(ofwca: OfwcaWithUser360Profiles): FormGroup {
    const coordinator = this.getDobryAgentCoordinator(ofwca);
    const hasUser360Account = this.hasUser360Account(ofwca);

    const group = this.fb.group({
      uuid: [ofwca.uuid],
      firstName: [ofwca.firstName ?? coordinator?.firstName ?? ''],
      lastName: [ofwca.lastName ?? coordinator?.lastName ?? ''],

      email: [
        coordinator?.email
        ?? ofwca.email
        ?? '',
        Validators.email
      ],

      phone: [
        coordinator?.phone
        ?? ofwca.phone
        ?? ''
      ],

      user24Id: [
        this.resolveOfwcaUser24Id(ofwca)
        ?? coordinator?.user24Id
        ?? null
      ],

      visibleOnDobryAgent: [!!coordinator && coordinator.active === true],

      blogAccess: [
        {
          value: this.hasDobryAgentBlogAccess(ofwca),
          disabled: !hasUser360Account,
        },
      ],

      businessCardEditAccess: [
        {
          value: this.hasDobryAgentBusinessCardEditAccess(ofwca),
          disabled: !hasUser360Account,
        },
      ],
    });

    return group;
  }

  private patchOfwcaPermissions(ofwcas: OfwcaWithUser360Profiles[]): void {
    this.ofwcaPermissionsArray.clear();

    (ofwcas ?? []).forEach((ofwca: OfwcaWithUser360Profiles) => {
      this.ofwcaPermissionsArray.push(this.buildOfwcaPermissionGroup(ofwca));
    });
  }

  private resolveOfwcaUser24Id(ofwca: any): string | null {
    const user360 = ofwca.user360 ?? [];

    if (!Array.isArray(user360) || user360.length === 0) {
      return null;
    }

    return user360[0]?.uuid ?? null;
  }

  private normalizeApplicationPermissions(applicationPermissions: any): any {
    const result = {};

    if (!applicationPermissions || !Array.isArray(applicationPermissions)) {
      return result;
    }

    applicationPermissions.forEach((item: any) => {
      if (item.applicationKey) {
        result[item.applicationKey] = item.permissions || {};
      }
    });

    return result;
  }

  private normalizeDepartmentApplicationPermissions(
    applicationPermissions: any,
    assignmentType: 'employee' | 'supervisor'
  ): any {
    const result = {};

    if (!applicationPermissions || !Array.isArray(applicationPermissions)) {
      return result;
    }

    applicationPermissions
      .filter((item: any) => item.assignmentType === assignmentType)
      .forEach((item: any) => {
        result[item.applicationKey] = item.permissions || {};
      });

    return result;
  }

  private getPrimaryUser360(element: any): User | null {
    if (!element?.user360Profiles || element.user360Profiles.length === 0) {
      return null;
    }

    return element.user360Profiles[0];
  }

  private getEffectiveDobryAgentPermissionsFromUser(user: User): any {
    if (!user) {
      return {};
    }

    const userPermissions = this.normalizeApplicationPermissions(user.applicationPermissions);
    const userDobryAgentPermissions = userPermissions?.dobryagent || {};

    let departmentDobryAgentPermissions: any = {};

    if (user.department?.applicationPermissions) {
      const departmentPermissions = this.normalizeDepartmentApplicationPermissions(
        user.department.applicationPermissions,
        'employee'
      );

      departmentDobryAgentPermissions = departmentPermissions?.dobryagent || {};
    }

    return {
      application_access:
        departmentDobryAgentPermissions?.application_access
        ?? userDobryAgentPermissions?.application_access
        ?? 'none',

      blog:
        userDobryAgentPermissions?.blog
        ?? 'none',

      business_card:
        userDobryAgentPermissions?.business_card
        ?? 'none',
    };
  }

  getDobryAgentPermission(element: any, module: string): string {
    const user = this.getPrimaryUser360(element);

    if (!user) {
      return 'none';
    }

    const permissions = this.getEffectiveDobryAgentPermissionsFromUser(user);

    return permissions?.[module] || 'none';
  }

  hasDobryAgentApplicationAccess(element: any): boolean {
    return this.getDobryAgentPermission(element, 'application_access') === 'active';
  }

  hasDobryAgentBlogAccess(element: any): boolean {
    if (!this.hasDobryAgentApplicationAccess(element)) {
      return false;
    }

    return this.getDobryAgentPermission(element, 'blog') === 'active';
  }

  hasDobryAgentBusinessCardEditAccess(element: any): boolean {
    if (!this.hasDobryAgentApplicationAccess(element)) {
      return false;
    }

    return this.getDobryAgentPermission(element, 'business_card') === 'active';
  }

  hasUser360Account(element: any): boolean {
    return !!element?.user360Profiles?.length;
  }

  private prepareAgentsPayload(ofwcaPermissions: any[]): any[] {
    return (ofwcaPermissions ?? [])
      .filter((item: any) => !!item.visibleOnDobryAgent)
      .map((item: any) => ({
        ofwca24Id: item.uuid,
        user24Id: item.user24Id ?? null,

        firstName: item.firstName ?? '',
        lastName: item.lastName ?? '',
        email: item.email ?? '',
        phone: item.phone ?? null,
        photo: item.photo ?? null,

        active: true,
        deleted: false,

        dobryAgentPermissions: {
          blog: item.blogAccess ? 'active' : 'none',
          business_card: item.businessCardEditAccess ? 'active' : 'none',
        },
      }))
      .filter((item: any) => !!item.email);
  }

  getDobryAgentCoordinator(ofwca: Ofwca): any | null {
    const coordinators = this.facilityDobryAgentObject?.coordinators ?? [];
    const ofwcaUuid = ofwca?.uuid;

    if (!ofwcaUuid) {
      return null;
    }

    return coordinators.find((coordinator: any) => {
      return coordinator.ofwca24Id === ofwcaUuid;
    }) ?? null;
  }

  isOfwcaActiveCoordinator(ofwca: Ofwca): boolean {
    const coordinator = this.getDobryAgentCoordinator(ofwca);

    return !!coordinator && coordinator.active === true;
  }

  private buildDobryAgentApplicationPermissionsPayload(
    user: User,
    blogAccess: boolean,
    businessCardEditAccess: boolean
  ): any {
    const currentPermissions = this.normalizeApplicationPermissions(user.applicationPermissions);

    const dobryAgentPermissions = {
      ...(currentPermissions?.dobryagent ?? {}),

      /**
       * Te dwa moduły są individual, więc aktualizujemy je bezpośrednio na użytkowniku.
       */
      blog: blogAccess ? 'active' : 'none',
      business_card: businessCardEditAccess ? 'active' : 'none',
    };

    return {
      ...currentPermissions,
      dobryagent: dobryAgentPermissions,
    };
  }

  private updateDobryAgentUsersPermissions(ofwcaPermissions: any[]): Observable<any[]> {
    const requests = (ofwcaPermissions ?? [])
      .map((item: any) => {
        const ofwca = this.ofwcasDataSource.data.find((dataItem: any) => {
          return dataItem.uuid === item.uuid;
        }) as any;

        const user = this.getPrimaryUser360(ofwca);

        if (!user?.uuid) {
          return null;
        }

        const payload = this.buildDobryAgentApplicationPermissionsPayload(
          user,
          !!item.blogAccess,
          !!item.businessCardEditAccess
        );

        return this.userService.updateApplicationPermissions(user.uuid, payload);
      })
      .filter((request: Observable<any> | null): request is Observable<any> => !!request);

    return requests.length ? forkJoin(requests) : of([]);
  }
  private mapFacilityImages(facility: any): ImageDragAndDropItem[] {
    if (Array.isArray(facility.images) && facility.images.length > 0) {
      return facility.images
        .slice()
        .sort((a: any, b: any) => (a.position ?? 999) - (b.position ?? 999))
        .map((image: any, index: number) => ({
          location: image.location ?? '',
          data: image.data ?? undefined,
          position: index + 1,
        }));
    }

    const images: ImageDragAndDropItem[] = [];

    if (facility.featuredImage?.src) {
      images.push({
        location: facility.featuredImage.location ?? facility.featuredImage.src,
        position: 1,
      });
    }

    if (Array.isArray(facility.galleryImage)) {
      facility.galleryImage.forEach((image: any) => {
        if (!image?.src) {
          return;
        }

        images.push({
          location: image.location ?? image.src,
          position: images.length + 1,
        });
      });
    }

    return images;
  }
  resolveDobryAgentAgentId(ofwca: Ofwca): string | null {
    const coordinator = this.getDobryAgentCoordinator(ofwca);

    return coordinator?.uuid ?? null;
  }

  isAgentImageUploading(element: Ofwca): boolean {
    const agentId = this.resolveDobryAgentAgentId(element);

    return !!agentId && !!this.uploadingAgentImage[agentId];
  }

  onAgentImageSelected(
    fileList: FileList | null,
    element: OfwcaWithUser360Profiles,
    index: number
  ): void {
    const file = fileList?.item(0);

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      this.snackBar.open('Wybrany plik nie jest obrazem', '', {
        duration: 2000,
      });

      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];

    if (!allowedTypes.includes(file.type)) {
      this.snackBar.open('Dozwolone formaty to JPG, PNG lub WEBP', '', {
        duration: 2500,
      });

      return;
    }

    const maxSize = 2 * 1024 * 1024;

    if (file.size > maxSize) {
      this.snackBar.open('Avatar może mieć maksymalnie 2 MB', '', {
        duration: 2500,
      });

      return;
    }

    const agentId = this.resolveDobryAgentAgentId(element);

    if (!agentId) {
      this.snackBar.open(
        'Najpierw zaznacz doradcę jako widocznego na portalu DA i zapisz wizytówkę.',
        '',
        { duration: 3500 }
      );

      return;
    }

    this.uploadingAgentImage[agentId] = true;

    this.facilityDobryAgentService.uploadAgentImage(agentId, file)
      .pipe(first())
      .subscribe({
        next: () => {
          this.uploadingAgentImage[agentId] = false;

          this.previewAgentImage(file, element, index);

          this.snackBar.open('Avatar doradcy został zaktualizowany', '', {
            duration: 2000,
          });
        },
        error: () => {
          this.uploadingAgentImage[agentId] = false;

          this.snackBar.open('Nie udało się wgrać avatara doradcy', '', {
            duration: 2500,
          });
        },
      });
  }

  private previewAgentImage(
    file: File,
    element: OfwcaWithUser360Profiles,
    index: number
  ): void {
    const reader = new FileReader();

    reader.onload = () => {
      const previewUrl = reader.result?.toString() ?? '';

      const data = [...this.ofwcasDataSource.data];

      data[index] = {
        ...data[index],
        avatarPreviewUrl: previewUrl,
      } as any;

      this.ofwcasDataSource.data = data;

      const group = this.getOfwcaPermissionGroup(index);

      group.get('photo')?.setValue(previewUrl);
    };

    reader.readAsDataURL(file);
  }

  getAgentAvatarUrl(element: OfwcaWithUser360Profiles): string {
    const previewUrl = (element as any)?.avatarPreviewUrl;

    if (previewUrl) {
      return previewUrl;
    }

    const coordinator = this.getDobryAgentCoordinator(element);

    const imageUrl =
      this.getImageVariantUrl(coordinator?.featuredImage, 'medium')
      ?? coordinator?.featuredImage?.src
      ?? this.getImageVariantUrl((element as any)?.featuredImage, 'medium')
      ?? (element as any)?.featuredImage?.src
      ?? '';

    if (!imageUrl) {
      return '';
    }

    if (
      imageUrl.startsWith('http://') ||
      imageUrl.startsWith('https://') ||
      imageUrl.startsWith('data:image')
    ) {
      return imageUrl;
    }

    return `${this.dobryAgentBaseUrl.replace(/\/$/, '')}/${imageUrl.replace(/^\//, '')}`;
  }
  private getImageVariantUrl(
    image: ImageResponsive | null | undefined,
    preferredSize: 'small' | 'medium' | 'large' | 'original' = 'medium'
  ): string | null {
    if (!image) {
      return null;
    }

    const preferredVariant = image.variants?.find(
      (variant) => variant.size === preferredSize
    );

    if (preferredVariant?.url) {
      return preferredVariant.url;
    }

    return image.src ?? null;
  }
  get daysOffFromWorkArray(): FormArray {
    return this.facilityDobryAgentForm.get('daysOffFromWork') as FormArray;
  }


  addSingleDayOff(): void {
    const date = this.normalizeDatepickerValue(this.dayOffDateControl.value);

    if (!date) {
      this.snackBar.open('Wybierz datę nieobecności', '', {
        duration: 2000,
      });

      return;
    }

    this.addDayOffDate(this.formatDateForPayload(date));

    this.dayOffDateControl.setValue(null);
  }

  addDayOffRange(): void {
    const from = this.normalizeDatepickerValue(this.dayOffDateFromControl.value);
    const to = this.normalizeDatepickerValue(this.dayOffDateToControl.value);

    if (!from || !to) {
      this.snackBar.open('Wybierz datę od i datę do', '', {
        duration: 2000,
      });

      return;
    }

    if (from.getTime() > to.getTime()) {
      this.snackBar.open('Data od nie może być późniejsza niż data do', '', {
        duration: 2500,
      });

      return;
    }

    const current = new Date(from);

    while (current.getTime() <= to.getTime()) {
      this.addDayOffDate(this.formatDateForPayload(current));
      current.setDate(current.getDate() + 1);
    }

    this.dayOffDateFromControl.setValue(null);
    this.dayOffDateToControl.setValue(null);
  }

  private normalizeDatepickerValue(value: any): Date | null {
    if (!value) {
      return null;
    }

    // Moment.js
    if (typeof value?.toDate === 'function') {
      return this.clearDateTime(value.toDate());
    }

    // Date
    if (value instanceof Date) {
      return this.clearDateTime(value);
    }

    // string: YYYY-MM-DD
    if (typeof value === 'string') {
      const [year, month, day] = value.split('-').map(Number);

      if (!year || !month || !day) {
        return null;
      }

      return new Date(year, month - 1, day);
    }

    return null;
  }
  removeDayOff(index: number): void {
    this.daysOffFromWorkArray.removeAt(index);
  }

  private addDayOffDate(date: string): void {
    const exists = this.daysOffFromWorkArray.controls.some((control) => {
      return control.value === date;
    });

    if (exists) {
      return;
    }

    this.daysOffFromWorkArray.push(this.fb.control(date));

    this.sortDaysOffFromWork();
  }

  private sortDaysOffFromWork(): void {
    const sortedDates = this.daysOffFromWorkArray.value
      .filter((date: string) => !!date)
      .sort();

    this.daysOffFromWorkArray.clear();

    sortedDates.forEach((date: string) => {
      this.daysOffFromWorkArray.push(this.fb.control(date));
    });
  }

  private clearDateTime(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private formatDateForPayload(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
  private patchDaysOffFromWork(daysOffFromWork: string[] | null | undefined): void {
    this.daysOffFromWorkArray.clear();

    (daysOffFromWork ?? [])
      .filter((date: string) => !!date)
      .sort()
      .forEach((date: string) => {
        this.daysOffFromWorkArray.push(this.fb.control(date));
      });
  }
}
